import PropTypes from 'prop-types'

import {useEffect, useState} from 'react'
import {Map} from 'immutable'

import GearIcon from '../../icons/GearIcon'
import ManageDeputyPermissionsToolTip from './ManageDeputyPermissionsToolTip'
import ManageLinkTooltip from './ManageLinkTooltip'
import Logger from '../../../../../lib/NewLogger'

import {corpMaxManageDeputyPermissionsPath} from '../../../../../lib/urlTools'
import {pluralize} from '../../../../../lib/tools'
import {setViewedFlagV2} from '../../../../../lib/userTools'
import {usePrevious} from '../../../../../lib/hooks'
import routerUtils from '../../../../../lib/routerUtils'

import './deputyPermissionsSummary.scss'

const DeputyPermissionsSummary = ({isDraftDeputy, ...otherProps}) => {
  const suppressToolTip = isDraftDeputy ?
    true :
    (otherProps.userConfig.get('account-manager') || otherProps.userConfig.get('admin'))

  const [showTooltip, setShowTooltip] = useState(!suppressToolTip)

  const [showFirstVisitTooltip, setShowFirstVisitTooltip] = useState(
    isDraftDeputy ?
      false :
      (otherProps.currentUser.getIn([
        'viewed-flags', 'has_seen_manage_button_tooltip'
      ]) || !otherProps.hasDeputyWithDeferredItems)
  )
  const prevHasDeputyWithDeferredItems = usePrevious(otherProps.hasDeputyWithDeferredItems)

  useEffect(() => {
    if (otherProps.hasDeputyWithDeferredItems !== prevHasDeputyWithDeferredItems && !suppressToolTip) {
      setShowFirstVisitTooltip(false)
      setShowTooltip(true)
    }
  }, [otherProps.hasDeputyWithDeferredItems, prevHasDeputyWithDeferredItems])

  const checkVisitToNewDeputyScreen = () => {
    /*
      Need these existence checks to fix a RollBar error that is thrown when either the user's session has timed out
      before this check is made or a user who is not logged in clicks a bookmark taking them to the My Deputies
      page. --Atanda and BLR
    */
    if (otherProps.currentUser) {
      if (showFirstVisitTooltip) {
        return !otherProps.currentUser.getIn([
          'viewed-flags', 'first_visit_to_after_death_screen'
        ])
      } else {
        return !otherProps.currentUser.getIn([
          'viewed-flags', 'has_seen_manage_button_tooltip'
        ])
      }
    } else {
      return false
    }
  }

  const isAllowedToUnlock = () =>
    otherProps.deputy.get('death-reporter') ||
    otherProps.deputy.getIn(['deputy-config', 'death-reporter'])

  const logEvent = ({name, context}) => {
    Logger.log({
      name,
      payload: {context}
    })
  }

  const hideTooltip = () => {
    if (showFirstVisitTooltip)
      setShowTooltip(false)
  }

  const updateUserFlag = flagName => {
    setViewedFlagV2({
      user: otherProps.currentUser,
      flagToSet: flagName,
      updateResource: otherProps.updateResource
    })
  }

  const dismissTooltip = () => {
    updateUserFlag(showFirstVisitTooltip ? 'first_visit_to_after_death_screen' : 'has_seen_manage_button_tooltip')
  }

  const managePermissionLink = () =>
    `${corpMaxManageDeputyPermissionsPath(
      otherProps.deputy.get('id')
    )}?context=my_deputies`

  const goToManagePermissions = () => {
    logEvent({
      name: 'clicked_deputy_permissions',
      context: 'deputy_permissions_all_items'
    })

    routerUtils.push(managePermissionLink())
  }

  const goToUnlockPermissions = () => {
    logEvent({
      name: 'clicked_deputy_permissions',
      context: 'deputy_permissions_unlocker'
    })

    routerUtils.push(
      `cm/deputies/${otherProps.deputy.get('id')}/manage-unlock-permissions`
    )
  }

  const permissionsSummaryItem = (count, text) => <div><strong>{count}</strong>{text}</div>

  const sadTooltipText = showFirstVisitTooltip ?
    "Tell us which items you want to share only after you're gone." :
    "Manage deputy's permission to unlock here."

  const immediatePermissions = () => {
    const numberOfReadPermissions = otherProps.deputy.getIn([
      'permissions', 'read'
    ]).size

    const messageText = isDraftDeputy ?
      'to be shared immediately' :
      'shared now'

    return permissionsSummaryItem(
      numberOfReadPermissions,
      ` ${pluralize('item', numberOfReadPermissions)} ${messageText}`
    )
  }

  const deferredPermissions = () => {
    const numberOfDeferredReadPermissions = otherProps.deputy.getIn([
      'permissions', 'deferred'
    ]).size

    const messageText = isDraftDeputy ?
      'to be shared after death' :
      'locked until after death'

    return permissionsSummaryItem(
      numberOfDeferredReadPermissions,
      ` ${pluralize('item', numberOfDeferredReadPermissions)} ${messageText}`
    )
  }

  const permissionToUnlockMessage = () => {
    if (isAllowedToUnlock()) {
      return isDraftDeputy ? 'Will be allowed to unlock' : 'Allowed to unlock'
    } else {
      return isDraftDeputy ?
        'Will not be allowed to unlock' :
        'Not allowed to unlock'
    }
  }

  return (
    <div className='deputy-permissions-summary flex-child flex-container deputy-item'>
      <section className='flex-child manage-permissions'>
        <div className='section-header-text'>Permissions</div>
        <div className='current-permissions thin-text'>
          <div className='sharing-summaries'>
            {immediatePermissions()}
            {otherProps.hasDeputyWithDeferredItems && deferredPermissions()}
          </div>
          {otherProps.hasDeputyWithDeferredItems && permissionToUnlockMessage()}
        </div>
      </section>
      {
        !isDraftDeputy && (
          <section className='action-bar flex-child flex-container'>
            {
              otherProps.hasDeputyWithDeferredItems ?
                (
                  <ManageDeputyPermissionsToolTip
                    goToManagePermissions={goToManagePermissions}
                    goToUnlockPermissions={goToUnlockPermissions}
                  />
                ) :
                <div
                  className='flex-container view-manage-permissions'
                  data-testid='view-manage-permissions'
                  onClick={goToManagePermissions}>
                  <GearIcon />
                  Manage
                </div>
            }
            <ManageLinkTooltip
              dismissTooltip={dismissTooltip}
              goToManagePermissions={goToManagePermissions}
              onClose={hideTooltip}
              onOpen={() => setShowTooltip(true)}
              sadTooltipText={sadTooltipText}
              showTooltip={showTooltip && checkVisitToNewDeputyScreen()}
            />
          </section>
        )
      }
    </div>
  )
}

DeputyPermissionsSummary.propTypes = {
  currentUser: PropTypes.instanceOf(Map),
  deputy: PropTypes.instanceOf(Map),
  hasDeputyWithDeferredItems: PropTypes.bool,
  isDraftDeputy: PropTypes.bool,
  updateResource: PropTypes.func,
  userConfig: PropTypes.instanceOf(Map)
}

export default DeputyPermissionsSummary
